import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import {
  clampDepthRange,
  clientYToDepth,
  findNearestDepth,
  getMinimumDepthSpan,
  normalizeDepths,
} from './sweetSpotDepth';
import type { DepthPlotGeometry, DepthRange } from './sweetSpotDepth';

type PanState = {
  startY: number;
  range: DepthRange;
  plotHeight: number;
};

type CursorDragState = {
  geometry: DepthPlotGeometry;
};

export function useDepthInteraction(depthValues: number[], resetKey?: unknown) {
  const depths = useMemo(() => normalizeDepths(depthValues), [depthValues]);
  const signature = useMemo(() => depths.join('|'), [depths]);
  const fullRange = useMemo<DepthRange | null>(() => depths.length
    ? [depths[0], depths[depths.length - 1]]
    : null, [depths]);
  const fullSpan = fullRange ? fullRange[1] - fullRange[0] : 0;
  const minimumSpan = useMemo(
    () => getMinimumDepthSpan(depths, fullSpan),
    [depths, fullSpan],
  );
  const canNavigate = depths.length > 1 && fullSpan > 0;

  const [visibleRange, setVisibleRange] = useState<DepthRange | null>(fullRange);
  const [selectedDepth, setSelectedDepth] = useState<number | null>(
    depths.length ? depths[Math.floor(depths.length / 2)] : null,
  );
  const [isPanningChart, setIsPanningChart] = useState(false);
  const [isDraggingCursor, setIsDraggingCursor] = useState(false);
  const panRef = useRef<PanState | null>(null);
  const cursorDragRef = useRef<CursorDragState | null>(null);

  useEffect(() => {
    if (!fullRange || !depths.length) {
      setVisibleRange(null);
      setSelectedDepth(null);
      return;
    }
    setVisibleRange(fullRange);
    setSelectedDepth(depths[Math.floor(depths.length / 2)]);
    panRef.current = null;
    cursorDragRef.current = null;
    setIsPanningChart(false);
    setIsDraggingCursor(false);
  }, [signature, resetKey]); // 数据源变化时重置到中间有效深度。

  const commitRange = useCallback((nextRange: DepthRange) => {
    if (!fullRange) return;
    const clamped = clampDepthRange(nextRange, fullRange, minimumSpan);
    setVisibleRange(clamped);
    setSelectedDepth((current) => {
      if (current == null) return findNearestDepth(depths, (clamped[0] + clamped[1]) / 2);
      if (current < clamped[0]) return findNearestDepth(depths, clamped[0]);
      if (current > clamped[1]) return findNearestDepth(depths, clamped[1]);
      return current;
    });
  }, [depths, fullRange, minimumSpan]);

  const selectAtClientY = useCallback((clientY: number, geometry: DepthPlotGeometry) => {
    if (!visibleRange || !depths.length) return;
    const target = clientYToDepth(clientY, visibleRange, geometry);
    setSelectedDepth(findNearestDepth(depths, target));
  }, [depths, visibleRange]);

  const zoomAt = useCallback((
    deltaY: number,
    clientY: number,
    geometry: DepthPlotGeometry,
  ) => {
    if (!canNavigate || !visibleRange || !fullRange) return;
    const plotTop = geometry.plotTop ?? 0;
    const plotBottom = geometry.plotBottom ?? 0;
    const plotHeight = Math.max(1, geometry.height - plotTop - plotBottom);
    const fraction = Math.max(
      0,
      Math.min(1, (clientY - geometry.top - plotTop) / plotHeight),
    );
    const span = visibleRange[1] - visibleRange[0];
    const nextSpan = Math.max(
      minimumSpan,
      Math.min(fullRange[1] - fullRange[0], span * (deltaY < 0 ? 0.8 : 1.25)),
    );
    const anchor = visibleRange[0] + fraction * span;
    commitRange([
      anchor - fraction * nextSpan,
      anchor + (1 - fraction) * nextSpan,
    ]);
  }, [canNavigate, commitRange, fullRange, minimumSpan, visibleRange]);

  const startPan = useCallback((clientY: number, plotHeight: number) => {
    if (!canNavigate || !visibleRange || cursorDragRef.current) return;
    panRef.current = { startY: clientY, range: visibleRange, plotHeight: Math.max(1, plotHeight) };
    setIsPanningChart(true);
  }, [canNavigate, visibleRange]);

  const startCursorDrag = useCallback((clientY: number, geometry: DepthPlotGeometry) => {
    if (!depths.length || !visibleRange) return;
    panRef.current = null;
    setIsPanningChart(false);
    cursorDragRef.current = { geometry };
    setIsDraggingCursor(true);
    selectAtClientY(clientY, geometry);
  }, [depths.length, selectAtClientY, visibleRange]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (cursorDragRef.current) {
        selectAtClientY(event.clientY, cursorDragRef.current.geometry);
        return;
      }
      if (!panRef.current) return;
      const { startY, range, plotHeight } = panRef.current;
      const span = range[1] - range[0];
      const offset = -((event.clientY - startY) / plotHeight) * span;
      commitRange([range[0] + offset, range[1] + offset]);
    };
    const handleMouseUp = () => {
      panRef.current = null;
      cursorDragRef.current = null;
      setIsPanningChart(false);
      setIsDraggingCursor(false);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [commitRange, selectAtClientY]);

  const resetView = useCallback(() => {
    if (!fullRange) return;
    setVisibleRange(fullRange);
    setSelectedDepth(depths[Math.floor(depths.length / 2)] ?? null);
  }, [depths, fullRange]);

  return {
    depths,
    fullRange,
    visibleRange: visibleRange ?? fullRange,
    selectedDepth,
    canNavigate,
    isPanningChart,
    isDraggingCursor,
    zoomAt,
    startPan,
    startCursorDrag,
    selectAtClientY,
    resetView,
  };
}

export function useDepthWheel<T extends HTMLElement>(
  elementRef: RefObject<T>,
  zoomAt: (
    deltaY: number,
    clientY: number,
    geometry: DepthPlotGeometry,
  ) => void,
  canNavigate: boolean,
  plotTop = 0,
  plotBottom = 0,
) {
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const handleWheel = (event: WheelEvent) => {
      if (canNavigate) event.preventDefault();
      const rect = element.getBoundingClientRect();
      zoomAt(event.deltaY, event.clientY, {
        top: rect.top,
        height: rect.height,
        plotTop,
        plotBottom,
      });
    };
    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => element.removeEventListener('wheel', handleWheel);
  }, [canNavigate, elementRef, plotBottom, plotTop, zoomAt]);
}
